import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createHolding,
  getAllPositionDetails,
  getBrokers,
  getPositions,
  refreshPricesBatch,
} from "../../services/portfolioService";
import { useCurrency } from "../Auth/AuthContext";
import CombinedPositionCard from "./CombinedPositionCard";
import PositionCard from "./PositionCard";

const PositionsPage = () => {
  const [positions, setPositions] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterBroker, setFilterBroker] = useState("");
  const [filterCurrency, setFilterCurrency] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("value");
  const [viewMode, setViewMode] = useState("by-broker"); // 'by-broker' or 'combined'
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [expandedBrokers, setExpandedBrokers] = useState([]); // For combined view
  const [positionDetails, setPositionDetails] = useState({});
  const [stockPrices, setStockPrices] = useState({});
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [sellFormData, setSellFormData] = useState({
    quantity: "",
    price: "",
    date: new Date().toISOString().split("T")[0],
    fees: "0",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(false);
  const navigate = useNavigate();
  const {
    selectedCurrency,
    convert,
    formatMoney,
    ratesReady,
    userCurrency,
    convertAndFormat,
  } = useCurrency();

  useEffect(() => {
    fetchData();
  }, [filterBroker]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fire all three in parallel — positions, brokers, and ALL transaction details
      const [positionsData, brokersData, allDetails] = await Promise.all([
        getPositions(filterBroker || null),
        getBrokers(),
        getAllPositionDetails(),
      ]);

      setPositions(positionsData);
      setBrokers(brokersData);
      setPositionDetails(allDetails); // already keyed as "TICKER-brokerid"

      console.log(allDetails);

      // Kick off price refresh in background (non-blocking)
      if (positionsData.length > 0) {
        fetchPrices(positionsData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load positions");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrices = async (positionsData) => {
    try {
      setPricesLoading(true);
      const uniqueTickers = [
        ...new Set(positionsData.map((p) => p.ticker_symbol)),
      ];
      const prices = await refreshPricesBatch(uniqueTickers);
      const pricesMap = {};
      prices.forEach((price) => {
        pricesMap[price.ticker_symbol] = price;
      });
      setStockPrices(pricesMap);
    } catch (error) {
      console.error("Error fetching prices:", error);
    } finally {
      setPricesLoading(false);
    }
  };

  const handleExpandPosition = (ticker, brokerId) => {
    const key = `${ticker}-${brokerId}`;
    setExpandedPosition(expandedPosition === key ? null : key);
  };

  const handleToggleBrokerExpand = (key) => {
    setExpandedBrokers((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleOpenSellDialog = (position) => {
    setSelectedPosition(position);
    setSellFormData({
      quantity: "",
      price: "",
      date: new Date().toISOString().split("T")[0],
      fees: "0",
      notes: "",
    });
    setSellDialogOpen(true);
  };

  const handleSellShares = async () => {
    if (!selectedPosition) return;

    if (!sellFormData.quantity || parseFloat(sellFormData.quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }
    if (
      parseFloat(sellFormData.quantity) >
      parseFloat(selectedPosition.net_quantity)
    ) {
      setError(
        `You only have ${formatQuantity(
          selectedPosition.net_quantity,
        )} shares available`,
      );
      return;
    }
    if (!sellFormData.price || parseFloat(sellFormData.price) <= 0) {
      setError("Please enter a valid sell price");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createHolding({
        broker_id: selectedPosition.broker_id,
        ticker_symbol: selectedPosition.ticker_symbol,
        company_name: selectedPosition.company_name,
        exchange: selectedPosition.exchange,
        transaction_type: "SELL",
        quantity: parseFloat(sellFormData.quantity),
        purchase_price: parseFloat(sellFormData.price),
        purchase_date: sellFormData.date,
        currency: selectedPosition.currency,
        fees: parseFloat(sellFormData.fees) || 0,
        notes: sellFormData.notes || `Sold ${sellFormData.quantity} shares`,
      });

      setSellDialogOpen(false);
      setSelectedPosition(null);
      setSuccess("Shares sold successfully!");
      setTimeout(() => setSuccess(null), 3000);
      await fetchData();
    } catch (error) {
      console.error("Error selling shares:", error);
      setError(error.response?.data?.message || "Failed to sell shares");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  const formatQuantity = (quantity) => {
    return parseFloat(quantity)
      .toFixed(4)
      .replace(/\.?0+$/, "");
  };

  const calculateGainLoss = (position) => {
    const price = stockPrices[position.ticker_symbol];
    if (!price) return null;

    const netQty = parseFloat(position.net_quantity) || 0;
    const currentPrice = parseFloat(price.current_price) || 0;
    const currentValue = netQty * currentPrice;

    // total_cost_average doesn't exist in the API response — total_cost is correct
    const costBasis = parseFloat(position.total_cost) || 0;

    const gainLoss = currentValue - costBasis;
    // Guard against divide-by-zero (e.g. pure dividend reinvest positions with 0 cost)
    const gainLossPercent = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;

    // price_change and price_change_percent can be null in the cache
    const priceChange =
      price.price_change != null ? parseFloat(price.price_change) : null;
    const priceChangePercent =
      price.price_change_percent != null
        ? parseFloat(price.price_change_percent)
        : null;

    let fiftyTwoWeekPosition = null;
    const high52 = price.fifty_two_week_high
      ? parseFloat(price.fifty_two_week_high)
      : null;
    const low52 = price.fifty_two_week_low
      ? parseFloat(price.fifty_two_week_low)
      : null;
    if (high52 != null && low52 != null && high52 !== low52) {
      fiftyTwoWeekPosition = ((currentPrice - low52) / (high52 - low52)) * 100;
    }

    return {
      currentPrice,
      currentValue,
      gainLoss,
      gainLossPercent,
      priceChange,
      priceChangePercent,
      fiftyTwoWeekHigh: high52,
      fiftyTwoWeekLow: low52,
      fiftyTwoWeekPosition,
    };
  };

  // Filter positions first
  const filteredPositions = positions.filter((position) => {
    const matchesCurrency =
      filterCurrency === "" || position.currency === filterCurrency;
    const matchesSearch =
      searchQuery === "" ||
      position.ticker_symbol
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (position.company_name &&
        position.company_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()));
    return matchesCurrency && matchesSearch;
  });

  // Create gainLoss map for all positions FIRST (needed for tickerTotals calculation)
  const allGainLoss = {};
  filteredPositions.forEach((position) => {
    const key = `${position.ticker_symbol}-${position.broker_id}`;
    allGainLoss[key] = calculateGainLoss(position);
  });

  // Group positions by ticker for combined view
  const groupedPositions = filteredPositions.reduce((acc, position) => {
    if (!acc[position.ticker_symbol]) {
      acc[position.ticker_symbol] = [];
    }
    acc[position.ticker_symbol].push(position);
    return acc;
  }, {});

  // Calculate totals for each ticker group (for combined view sorting)
  const tickerTotals = Object.entries(groupedPositions).map(
    ([ticker, positions]) => {
      const totalQuantity = positions.reduce(
        (sum, p) => sum + parseFloat(p.net_quantity),
        0,
      );
      const totalCost = positions.reduce(
        (sum, p) => sum + parseFloat(p.total_cost),
        0,
      );

      const firstGainLoss =
        allGainLoss[`${positions[0].ticker_symbol}-${positions[0].broker_id}`];
      const totalValue = firstGainLoss
        ? totalQuantity * firstGainLoss.currentPrice
        : totalCost;
      const totalGain = totalValue - totalCost;
      const totalGainPercent = (totalGain / totalCost) * 100;

      return {
        ticker,
        positions,
        totalQuantity,
        totalCost,
        totalValue,
        totalGain,
        totalGainPercent,
      };
    },
  );

  // Sort based on view mode
  const sortedData =
    viewMode === "combined"
      ? tickerTotals.sort((a, b) => {
          switch (sortBy) {
            case "value":
              return b.totalValue - a.totalValue;
            case "gain":
              return b.totalGainPercent - a.totalGainPercent;
            case "ticker":
              return a.ticker.localeCompare(b.ticker);
            case "quantity":
              return b.totalQuantity - a.totalQuantity;
            default:
              return 0;
          }
        })
      : filteredPositions.sort((a, b) => {
          const gainLossA = calculateGainLoss(a);
          const gainLossB = calculateGainLoss(b);

          switch (sortBy) {
            case "value":
              const valueA =
                gainLossA?.currentValue || parseFloat(a.total_cost);
              const valueB =
                gainLossB?.currentValue || parseFloat(b.total_cost);
              return valueB - valueA;
            case "gain":
              const gainA = gainLossA?.gainLossPercent || 0;
              const gainB = gainLossB?.gainLossPercent || 0;
              return gainB - gainA;
            case "ticker":
              return a.ticker_symbol.localeCompare(b.ticker_symbol);
            case "quantity":
              return parseFloat(b.net_quantity) - parseFloat(a.net_quantity);
            default:
              return 0;
          }
        });

  const currencies = [...new Set(positions.map((p) => p.currency))];

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        {pricesLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Refreshing prices…
          </Typography>
        )}
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={600}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Open Positions
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate("/stock-transactions")}
            >
              View Transactions
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/add-stock-transaction")}
            >
              Add Transaction
            </Button>
          </Box>
        </Box>

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters and View Toggle */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by ticker or company..."
            sx={{ minWidth: 250 }}
            size="small"
          />

          <TextField
            select
            label="Sort By"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="value">Current Value (High to Low)</MenuItem>
            <MenuItem value="gain">Gain % (High to Low)</MenuItem>
            <MenuItem value="ticker">Ticker (A-Z)</MenuItem>
            <MenuItem value="quantity">Quantity (High to Low)</MenuItem>
          </TextField>

          <TextField
            select
            label="Filter by Broker"
            value={filterBroker}
            onChange={(e) => setFilterBroker(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="">All Brokers</MenuItem>
            {brokers.map((broker) => (
              <MenuItem key={broker.id} value={broker.id}>
                {broker.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Filter by Currency"
            value={filterCurrency}
            onChange={(e) => setFilterCurrency(e.target.value)}
            sx={{ minWidth: 180 }}
            size="small"
          >
            <MenuItem value="">All Currencies</MenuItem>
            {currencies.map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ marginLeft: "auto" }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="by-broker">
                <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                By Broker
              </ToggleButton>
              <ToggleButton value="combined">
                <ViewModuleIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Combined
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* No Positions */}
        {(viewMode === "by-broker"
          ? filteredPositions.length
          : tickerTotals.length) === 0 ? (
          <Box textAlign="center" py={6}>
            <TrendingUpIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No positions found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {positions.length === 0
                ? "You don't have any open positions yet."
                : "No positions match your filters."}
            </Typography>
            {positions.length === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/add-stock-transaction")}
              >
                Add Your First Transaction
              </Button>
            )}
          </Box>
        ) : (
          <>
            {/* BY BROKER VIEW */}
            {viewMode === "by-broker" && (
              <Grid container spacing={1.5}>
                {sortedData.map((position) => {
                  const key = `${position.ticker_symbol}-${position.broker_id}`;
                  const isExpanded = expandedPosition === key;
                  const details = positionDetails[key];
                  const gainLoss = calculateGainLoss(position);

                  return (
                    <Grid item xs={12} key={key}>
                      <PositionCard
                        position={position}
                        gainLoss={gainLoss}
                        isExpanded={isExpanded}
                        onExpand={() =>
                          handleExpandPosition(
                            position.ticker_symbol,
                            position.broker_id,
                          )
                        }
                        onSell={() => handleOpenSellDialog(position)}
                        onAddMore={() => navigate("/add-stock-transaction")}
                        formatCurrency={formatCurrency}
                        formatQuantity={formatQuantity}
                      />

                      {/* Transaction Details */}
                      {isExpanded && details && (
                        <Collapse in={isExpanded}>
                          <Box
                            mt={1.5}
                            p={2}
                            bgcolor="grey.50"
                            borderRadius={1}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Transaction History ({details.length}{" "}
                              transactions)
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell align="right">Quantity</TableCell>
                                  <TableCell align="right">Price</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                  <TableCell>Notes</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {details.map((transaction) => (
                                  <TableRow key={transaction.id}>
                                    <TableCell>
                                      {format(
                                        new Date(transaction.purchase_date),
                                        "MMM dd, yyyy",
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={transaction.transaction_type}
                                        size="small"
                                        color={
                                          transaction.transaction_type === "BUY"
                                            ? "success"
                                            : "error"
                                        }
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatQuantity(transaction.quantity)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {convertAndFormat(
                                        transaction.purchase_price,
                                        transaction.currency,
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {convertAndFormat(
                                        transaction.total_cost,
                                        transaction.currency,
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {transaction.notes || "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* COMBINED VIEW */}
            {viewMode === "combined" && (
              <Grid container spacing={1.5}>
                {sortedData.map(({ ticker, positions: tickerPositions }) => (
                  <Grid item xs={12} key={ticker}>
                    <CombinedPositionCard
                      ticker={ticker}
                      positions={tickerPositions}
                      allGainLoss={allGainLoss}
                      expandedBrokers={expandedBrokers}
                      positionDetails={positionDetails}
                      onToggleBrokerExpand={handleToggleBrokerExpand}
                      onAddMore={() => navigate("/add-stock-transaction")}
                      formatCurrency={formatCurrency}
                      formatQuantity={formatQuantity}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {viewMode === "by-broker" ? (
                  <>
                    Showing {sortedData.length} position
                    {sortedData.length !== 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    Showing {sortedData.length} unique ticker
                    {sortedData.length !== 1 ? "s" : ""} (
                    {filteredPositions.length} total positions)
                  </>
                )}
              </Typography>
            </Box>
          </>
        )}
      </Paper>

      {/* Sell Dialog */}
      <Dialog
        open={sellDialogOpen}
        onClose={() => !submitting && setSellDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sell {selectedPosition?.ticker_symbol} Shares</DialogTitle>
        <DialogContent>
          {selectedPosition && (
            <Box mt={2}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Available: {formatQuantity(selectedPosition.net_quantity)}{" "}
                shares
              </Alert>

              <TextField
                autoFocus
                label="Quantity to Sell *"
                type="number"
                fullWidth
                margin="normal"
                value={sellFormData.quantity}
                onChange={(e) =>
                  setSellFormData({ ...sellFormData, quantity: e.target.value })
                }
                disabled={submitting}
                inputProps={{
                  step: "0.000001",
                  min: "0",
                  max: selectedPosition.net_quantity,
                }}
              />

              <TextField
                label="Sell Price per Share *"
                type="number"
                fullWidth
                margin="normal"
                value={sellFormData.price}
                onChange={(e) =>
                  setSellFormData({ ...sellFormData, price: e.target.value })
                }
                disabled={submitting}
                inputProps={{ step: "0.01", min: "0" }}
              />

              <TextField
                label="Date"
                type="date"
                fullWidth
                margin="normal"
                value={sellFormData.date}
                onChange={(e) =>
                  setSellFormData({ ...sellFormData, date: e.target.value })
                }
                disabled={submitting}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Fees"
                type="number"
                fullWidth
                margin="normal"
                value={sellFormData.fees}
                onChange={(e) =>
                  setSellFormData({ ...sellFormData, fees: e.target.value })
                }
                disabled={submitting}
                inputProps={{ step: "0.01", min: "0" }}
              />

              <TextField
                label="Notes"
                fullWidth
                margin="normal"
                multiline
                rows={2}
                value={sellFormData.notes}
                onChange={(e) =>
                  setSellFormData({ ...sellFormData, notes: e.target.value })
                }
                disabled={submitting}
              />

              {sellFormData.quantity && sellFormData.price && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Estimated Proceeds:</strong>{" "}
                    {formatCurrency(
                      parseFloat(sellFormData.quantity) *
                        parseFloat(sellFormData.price) -
                        parseFloat(sellFormData.fees || 0),
                      selectedPosition.currency,
                    )}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSellDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSellShares}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : "Sell Shares"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PositionsPage;
